import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  erpFetch,
  isErpApiSuccess,
  getErpRequestErrorMessage,
  getActiveBranchId,
} from "../../erpClient";
import { formatIndianRupee } from "../../utils/formatLiveRate";
import { useButtonTableStyle } from "../../utils/useButtonTableStyle";

function dashNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function StatCard({ icon, label, value, isRupee, color, bgLight, subtitle, loading, onClick }) {
  return (
    <div className="col-12 col-sm-6 col-lg-4 col-xl-2">
      <div
        className="card shadow-none h-100 border-0 transition-all"
        style={{
          minHeight: "130px",
          background: "#FFFFFF",
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          cursor: onClick ? "pointer" : "default",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
        }}
        onClick={onClick}
      >
        <div className="card-body p-3 d-flex flex-column justify-content-between h-100">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span
              style={{
                fontSize: "0.8rem",
                color: "#64748B",
                fontWeight: 600,
                letterSpacing: "0.2px",
              }}
            >
              {label}
            </span>
            <div
              className="d-flex align-items-center justify-content-center rounded-3"
              style={{
                width: 36,
                height: 36,
                background: bgLight,
                color: color,
                flexShrink: 0,
              }}
            >
              <Icon icon={icon} width={20} />
            </div>
          </div>

          <div>
            {loading ? (
              <div
                className="placeholder-glow"
                style={{
                  height: 28,
                  background: "#F1F5F9",
                  borderRadius: 6,
                  width: "60%",
                }}
              />
            ) : (
              <h4
                className="mb-1"
                style={{
                  fontSize: isRupee ? "1.45rem" : "1.75rem",
                  fontWeight: 700,
                  color: "#0F172A",
                  lineHeight: 1.2,
                }}
              >
                {isRupee ? formatIndianRupee(value) : value.toLocaleString("en-IN")}
              </h4>
            )}
            {subtitle && (
              <span className="text-muted" style={{ fontSize: "0.725rem" }}>
                {subtitle}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SchemeDashboard() {
  const navigate = useNavigate();
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const branchId = getActiveBranchId() || 1;
      const res = await erpFetch("/scheme", {
        method: "POST",
        body: { type: "dashboard", branch_id: branchId },
      });

      if (isErpApiSuccess(res)) {
        setData(res.data ?? res);
      } else {
        toast.error(res?.message || "Failed to load scheme dashboard.");
      }
    } catch (err) {
      toast.error(
        getErpRequestErrorMessage(err, "Failed to load scheme dashboard.")
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const handler = () => fetchDashboard();
    window.addEventListener("branchChanged", handler);
    return () => window.removeEventListener("branchChanged", handler);
  }, [fetchDashboard]);

  const cards = [
    {
      icon: "solar:star-circle-line-duotone",
      label: "Active Cards",
      key: "active_count",
      color: "#0d6efd",
      bgLight: "#EFF6FF",
      subtitle: "Ongoing installments",
      route: "/scheme/cards",
    },
    {
      icon: "solar:check-circle-line-duotone",
      label: "Matured Cards",
      key: "matured_count",
      color: "#198754",
      bgLight: "#F0FDF4",
      subtitle: "Ready for purchase",
      route: "/scheme/cards",
    },
    {
      icon: "solar:gift-line-duotone",
      label: "Redeemed Cards",
      key: "redeemed_count",
      color: "#6f42c1",
      bgLight: "#FAF5FF",
      subtitle: "Purchases completed",
      route: "/scheme/cards",
    },
    {
      icon: "solar:clock-circle-line-duotone",
      label: "Due Installments",
      key: "due_count",
      color: "#dc3545",
      bgLight: "#FEF2F2",
      subtitle: "Pending payment collection",
      route: "/scheme/cards",
    },
    {
      icon: "solar:hand-money-line-duotone",
      label: "Collected Today",
      key: "collected_today",
      isRupee: true,
      color: "#0891b2",
      bgLight: "#ECFEFF",
      subtitle: "Daily receipts total",
      route: "/scheme/transactions",
    },
    {
      icon: "solar:wallet-money-line-duotone",
      label: "Scheme Liability",
      key: "available_liability",
      isRupee: true,
      color: "#d97706",
      bgLight: "#FFFBEB",
      subtitle: "Total customer balance",
      route: "/scheme/cards",
    },
  ];

  return (
    <div className="body-wrapper">
      <div className="container-fluid py-4">
        {/* Top Header Card */}
        <div className={`card w-100 position-relative overflow-hidden mb-4 ${cardClass}`}>
          <div className="px-4 py-3 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h4 className="card-title mb-1">Scheme Dashboard</h4>
              <p className="text-muted small mb-0">
                Monitor gold & savings scheme cards, maturities, receipts, and customer liabilities.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className={`btn btn-outline-secondary ${buttonClass}`}
                onClick={fetchDashboard}
                disabled={loading}
              >
                <Icon
                  icon="solar:refresh-line-duotone"
                  className={`me-1 ${loading ? "spin" : ""}`}
                />
                Refresh
              </button>
              <button
                type="button"
                className={`btn btn-secondary ${buttonClass}`}
                onClick={() => navigate("/scheme/plans")}
              >
                <Icon icon="solar:document-text-line-duotone" className="me-1" />
                Scheme Plans
              </button>
              <button
                type="button"
                className={`btn btn-info ${buttonClass}`}
                onClick={() => navigate("/scheme/transactions")}
              >
                <Icon icon="solar:bill-list-line-duotone" className="me-1" />
                Transactions
              </button>
              <button
                type="button"
                className={`btn btn-primary ${buttonClass}`}
                onClick={() => navigate("/scheme/cards")}
              >
                <Icon icon="solar:card-2-line-duotone" className="me-1" />
                Scheme Cards
              </button>
            </div>
          </div>
        </div>

        {/* 6 Key Stat Cards */}
        <div className="row g-3 mb-4">
          {cards.map((c) => (
            <StatCard
              key={c.key}
              icon={c.icon}
              label={c.label}
              value={dashNum(data?.[c.key])}
              isRupee={!!c.isRupee}
              color={c.color}
              bgLight={c.bgLight}
              subtitle={c.subtitle}
              loading={loading}
              onClick={() => c.route && navigate(c.route)}
            />
          ))}
        </div>

        {/* Quick Hub Navigation Cards */}
        <div className="row g-4">
          <div className="col-12 col-lg-8">
            <div className={`card h-100 border-0 shadow-sm ${cardClass}`} style={{ border: "1px solid #E2E8F0" }}>
              <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0 fs-4">Scheme Operations</h5>
                <span className="badge bg-primary-subtle text-primary">Quick Access</span>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <div
                      className="p-3 rounded-3 border d-flex align-items-center justify-content-between cursor-pointer"
                      style={{ background: "#F8FAFC", cursor: "pointer" }}
                      onClick={() => navigate("/scheme/cards")}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="rounded-3 d-flex align-items-center justify-content-center"
                          style={{ width: 44, height: 44, background: "#EFF6FF", color: "#2563EB" }}
                        >
                          <Icon icon="solar:user-plus-rounded-line-duotone" width={24} />
                        </div>
                        <div>
                          <div className="fw-semibold text-dark">Enroll New Customer</div>
                          <div className="text-muted small">Issue a new scheme card under any active plan</div>
                        </div>
                      </div>
                      <Icon icon="solar:alt-arrow-right-line-duotone" width={20} className="text-muted" />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div
                      className="p-3 rounded-3 border d-flex align-items-center justify-content-between cursor-pointer"
                      style={{ background: "#F8FAFC", cursor: "pointer" }}
                      onClick={() => navigate("/scheme/cards")}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="rounded-3 d-flex align-items-center justify-content-center"
                          style={{ width: 44, height: 44, background: "#ECFDF5", color: "#059669" }}
                        >
                          <Icon icon="solar:card-recive-line-duotone" width={24} />
                        </div>
                        <div>
                          <div className="fw-semibold text-dark">Receive Payment</div>
                          <div className="text-muted small">Record monthly installment receipts with mode</div>
                        </div>
                      </div>
                      <Icon icon="solar:alt-arrow-right-line-duotone" width={20} className="text-muted" />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div
                      className="p-3 rounded-3 border d-flex align-items-center justify-content-between cursor-pointer"
                      style={{ background: "#F8FAFC", cursor: "pointer" }}
                      onClick={() => navigate("/scheme/plans")}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="rounded-3 d-flex align-items-center justify-content-center"
                          style={{ width: 44, height: 44, background: "#FAF5FF", color: "#7C3AED" }}
                        >
                          <Icon icon="solar:settings-line-duotone" width={24} />
                        </div>
                        <div>
                          <div className="fw-semibold text-dark">Manage Scheme Plans</div>
                          <div className="text-muted small">Configure fixed/flexible amounts & bonus months</div>
                        </div>
                      </div>
                      <Icon icon="solar:alt-arrow-right-line-duotone" width={20} className="text-muted" />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div
                      className="p-3 rounded-3 border d-flex align-items-center justify-content-between cursor-pointer"
                      style={{ background: "#F8FAFC", cursor: "pointer" }}
                      onClick={() => navigate("/scheme/transactions")}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="rounded-3 d-flex align-items-center justify-content-center"
                          style={{ width: 44, height: 44, background: "#FFFBEB", color: "#D97706" }}
                        >
                          <Icon icon="solar:history-line-duotone" width={24} />
                        </div>
                        <div>
                          <div className="fw-semibold text-dark">Transaction Ledger</div>
                          <div className="text-muted small">Audit all installments, receipts, and payment modes</div>
                        </div>
                      </div>
                      <Icon icon="solar:alt-arrow-right-line-duotone" width={20} className="text-muted" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className={`card h-100 border-0 shadow-sm ${cardClass}`} style={{ border: "1px solid #E2E8F0" }}>
              <div className="px-4 py-3 border-bottom">
                <h5 className="card-title mb-0 fs-4">Scheme Flow Guide</h5>
              </div>
              <div className="card-body p-4">
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex gap-3 align-items-start">
                    <div
                      className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center fw-bold fs-3"
                      style={{ width: 32, height: 32, flexShrink: 0 }}
                    >
                      1
                    </div>
                    <div>
                      <div className="fw-semibold text-dark">Plan Setup</div>
                      <div className="text-muted small">Create 11+1 or flexible savings schemes with bonus months or percentage.</div>
                    </div>
                  </div>

                  <div className="d-flex gap-3 align-items-start">
                    <div
                      className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center fw-bold fs-3"
                      style={{ width: 32, height: 32, flexShrink: 0 }}
                    >
                      2
                    </div>
                    <div>
                      <div className="fw-semibold text-dark">Enrollment & Payments</div>
                      <div className="text-muted small">Assign card to customer; collect installments monthly in cash/bank/UPI.</div>
                    </div>
                  </div>

                  <div className="d-flex gap-3 align-items-start">
                    <div
                      className="rounded-circle bg-info-subtle text-info d-flex align-items-center justify-content-center fw-bold fs-3"
                      style={{ width: 32, height: 32, flexShrink: 0 }}
                    >
                      3
                    </div>
                    <div>
                      <div className="fw-semibold text-dark">Maturity & Redemption</div>
                      <div className="text-muted small">On completing tenure, bonus applies automatically for jewelry purchase redemption.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SchemeDashboard;
